import React, { useState, useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import useSchools from "MyCalendar/hooks/useSchools";
import useSchoolSelection from "AppComponents/SharedEntities/SelectParticipantsModal/hooks/useSchoolSelection";
import { useShallowSelector } from "Hooks";
import {
  getUserId,
  getOrganizationCurriculumPrograms,
} from "modules/CommonSelectors";
import {
  getUserEventCreationAccessLevel,
  getSelectedSchoolDetailsForCalendar,
} from "MyCalendar/modules/Utils";
import ACLStorage from "lib/aclService";
import { CAN_MANAGE_CALENDAR_EVENTS_AND_MEETINGS_PERMISSION } from "Constants/permissionConstants";
import { CoursesContext } from "./context/CoursesContext";
import {
  useCourseHierarchy,
  useVisibleEntities,
  useCourseDetails,
  useEntityCounts,
} from "./hooks";
import {
  filterCoursesBySearch,
  getFilteredSchoolCourseIds,
} from "./modules/CoursesUtils";

const DEFAULT_USER_TYPES = ["STAFF", "STUDENT", "FAMILY_MEMBER"];

const CoursesProvider = ({
  children,
  organizationId,
  academicYearId,
  selectedUserTypes,
  viewOnly,
  isActive,
  onSelectionChange,
  // External state overrides (lifted from SelectParticipantsContext when inside modal)
  selectedCourseList: externalSelectedCourseList,
  setSelectedCourseList: externalSetSelectedCourseList,
  searchText: externalSearchText,
  setSearchText: externalSetSearchText,
  selectedSchoolIds: externalSelectedSchoolIds,
  setSelectedSchoolIds: externalSetSelectedSchoolIds,
}) => {
  // 1. Admin status + userId from Redux
  // organizationCurriculumPrograms is kept as a stable Redux reference — do NOT
  // filter inside the selector because _.filter always returns a new array,
  // causing useShallowSelector to see a changed reference every render and loop.
  const {
    userId,
    userCanCreateHolidaysAndWholeSchoolEvents,
    userHasAccountLevelAccess,
    organizationCurriculumPrograms,
  } = useShallowSelector(state => {
    const { currentCurriculumProgramId } = getSelectedSchoolDetailsForCalendar(
      state
    );
    const userEventCreationAccessLevel = getUserEventCreationAccessLevel({
      currentCurriculumProgramId,
      alsoCheckClassLevelPermission: false,
    });
    return {
      userId: getUserId(state),
      organizationCurriculumPrograms: getOrganizationCurriculumPrograms(state),
      userCanCreateHolidaysAndWholeSchoolEvents: _.includes(
        ["ACCOUNT_LEVEL", "PROGRAM_LEVEL"],
        userEventCreationAccessLevel
      ),
      userHasAccountLevelAccess:
        userEventCreationAccessLevel === "ACCOUNT_LEVEL",
    };
  });

  // Filtered outside the selector so _.filter's new-array-each-call doesn't
  // break useShallowSelector's reference equality check.
  const programsUserCanPublishTo = useMemo(
    () =>
      _.filter(organizationCurriculumPrograms, ({ id }) =>
        ACLStorage.getPermissionValueByContext(
          CAN_MANAGE_CALENDAR_EVENTS_AND_MEETINGS_PERMISSION,
          [{ entityId: id, entityType: "CURRICULUM_PROGRAM" }]
        )
      ),
    [organizationCurriculumPrograms]
  );

  // 2. Schools from Redux
  const { schools } = useSchools();

  // 3. School selection — use external state if provided, otherwise manage internally.
  // null = "all schools" sentinel; a Set means a specific subset is selected.
  const [internalSchoolIds, setInternalSchoolIds] = useState(null);
  const selectedSchoolIds =
    externalSelectedSchoolIds !== undefined
      ? externalSelectedSchoolIds
      : internalSchoolIds;
  const setSelectedSchoolIds =
    externalSetSelectedSchoolIds || setInternalSchoolIds;

  // Initialise for single-school orgs when schools load from Redux.
  // Skip when external state is provided — the caller owns school selection in that case.
  useEffect(() => {
    if (externalSelectedSchoolIds !== undefined) return;
    if (_.size(schools) === 1) {
      setSelectedSchoolIds(new Set([_.get(schools, "0.id")]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schools]);

  const {
    onSchoolChange,
    isAllSchoolsSelected,
    selectedSchoolLabel,
    allSchoolIds,
  } = useSchoolSelection(schools, selectedSchoolIds, setSelectedSchoolIds);

  // 3. Selection + search state — use external overrides if provided
  // (resolved early so hooks below can use searchText)
  const [internalSelectedCourseList, setInternalSelectedCourseList] = useState(
    []
  );
  const [internalSearchText, setInternalSearchText] = useState("");

  const selectedCourseList =
    externalSelectedCourseList !== undefined
      ? externalSelectedCourseList
      : internalSelectedCourseList;
  const resolvedSetSelectedCourseList =
    externalSetSelectedCourseList || setInternalSelectedCourseList;
  const searchText =
    externalSearchText !== undefined ? externalSearchText : internalSearchText;
  const resolvedSetSearchText = externalSetSearchText || setInternalSearchText;

  // 4. Phase 1: Fetch all IDs, build hierarchy
  const {
    hierarchy,
    allGradeIds,
    allCourseIds,
    loading: hierarchyLoading,
  } = useCourseHierarchy(organizationId, !isActive, academicYearId, {
    userId,
    isAdmin: userCanCreateHolidaysAndWholeSchoolEvents,
    courseFilters: !userHasAccountLevelAccess
      ? {
          curriculumProgramIds: _.map(programsUserCanPublishTo, ({ id }) => id),
        }
      : undefined,
  });

  // 5. Visible course IDs — driven by Virtuoso rangeChanged in CoursesList
  const [visibleCourseIds, setVisibleCourseIds] = useState([]);
  const { registerCourseRef } = useVisibleEntities(true); // observer disabled; range tracked via Virtuoso

  // 6. Phase 2: Fetch details for visible courses.
  // When searching, fetch all course IDs so search can match against all titles,
  // including courses that haven't scrolled into view yet.
  const allCourseIdsForSearch = searchText ? allCourseIds : undefined;
  const { courseDetailsMap, loading: detailsLoading } = useCourseDetails(
    visibleCourseIds,
    viewOnly,
    allCourseIdsForSearch
  );

  // 7. Entity counts (course-level + all-schools aggregate)
  const {
    courseCountMap,
    allSchoolsCount,
    loading: countsLoading,
    allSchoolsCountLoading,
  } = useEntityCounts({
    visibleCourseIds,
    organizationId,
    allSchoolIds,
    viewOnly,
    selectedSchoolIds,
    isActive,
  });

  // Notify parent on selection change.
  // Intentionally omit onSelectionChange from deps — we only want to fire when
  // the selection itself changes, not when the callback reference changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    onSelectionChange(selectedCourseList);
  }, [selectedCourseList]);

  // Restrict to accessible schools — hierarchy may contain schools the user can't
  // see (e.g. PROGRAM_LEVEL admin whose org has more schools than they manage).
  // When "All Schools" is selected (null), use the accessible school set so
  // getFilteredSchoolCourseIds never returns courses outside the user's schools.
  // Keep null until allSchoolIds loads to avoid filtering out all courses on initial render.
  const effectiveSchoolIds = useMemo(
    () =>
      selectedSchoolIds ?? (allSchoolIds.length > 0 ? new Set(allSchoolIds) : null),
    [selectedSchoolIds, allSchoolIds]
  );

  // Course IDs scoped to the currently selected (and accessible) schools
  const filteredSchoolCourseIds = useMemo(
    () =>
      getFilteredSchoolCourseIds(hierarchy, effectiveSchoolIds, allCourseIds),
    [hierarchy, effectiveSchoolIds, allCourseIds]
  );

  // Filtered course IDs based on search, scoped to selected schools
  const filteredCourseIds = useMemo(
    () =>
      filterCoursesBySearch(
        filteredSchoolCourseIds,
        courseDetailsMap,
        searchText
      ),
    [filteredSchoolCourseIds, courseDetailsMap, searchText]
  );

  // Stable setters wrapped in useCallback
  const handleSetSelectedCourseList = useCallback(
    updater => resolvedSetSelectedCourseList(updater),
    [resolvedSetSelectedCourseList]
  );

  const handleSetSearchText = useCallback(text => resolvedSetSearchText(text), [
    resolvedSetSearchText,
  ]);

  // 8. Memoized context value
  const contextValue = useMemo(
    () => ({
      organizationId,
      selectedUserTypes,
      viewOnly,
      hierarchy,
      allSchoolIds,
      allGradeIds,
      allCourseIds,
      hierarchyLoading,
      selectedSchoolIds,
      isAllSchoolsSelected,
      onSchoolChange,
      selectedSchoolLabel,
      schools,
      visibleCourseIds,
      setVisibleCourseIds,
      registerCourseRef,
      courseDetailsMap,
      detailsLoading,
      courseCountMap,
      allSchoolsCount,
      countsLoading,
      allSchoolsCountLoading,
      selectedCourseList,
      setSelectedCourseList: handleSetSelectedCourseList,
      searchText,
      setSearchText: handleSetSearchText,
      filteredCourseIds,
      filteredSchoolCourseIds,
    }),
    [
      organizationId,
      selectedUserTypes,
      viewOnly,
      hierarchy,
      allSchoolIds,
      allGradeIds,
      allCourseIds,
      hierarchyLoading,
      selectedSchoolIds,
      isAllSchoolsSelected,
      onSchoolChange,
      selectedSchoolLabel,
      schools,
      visibleCourseIds,
      setVisibleCourseIds,
      registerCourseRef,
      courseDetailsMap,
      detailsLoading,
      courseCountMap,
      allSchoolsCount,
      countsLoading,
      allSchoolsCountLoading,
      selectedCourseList,
      handleSetSelectedCourseList,
      searchText,
      handleSetSearchText,
      filteredCourseIds,
      filteredSchoolCourseIds,
    ]
  );

  return (
    <CoursesContext.Provider value={contextValue}>
      {children}
    </CoursesContext.Provider>
  );
};

CoursesProvider.propTypes = {
  children: PropTypes.node.isRequired,
  organizationId: PropTypes.string.isRequired,
  academicYearId: PropTypes.string,
  selectedUserTypes: PropTypes.arrayOf(PropTypes.string),
  viewOnly: PropTypes.bool,
  isActive: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  selectedCourseList: PropTypes.array,
  setSelectedCourseList: PropTypes.func,
  searchText: PropTypes.string,
  setSearchText: PropTypes.func,
  selectedSchoolIds: PropTypes.instanceOf(Set),
  setSelectedSchoolIds: PropTypes.func,
};

CoursesProvider.defaultProps = {
  academicYearId: undefined,
  selectedUserTypes: DEFAULT_USER_TYPES,
  viewOnly: false,
  isActive: true,
  onSelectionChange: () => {},
  selectedCourseList: undefined,
  setSelectedCourseList: undefined,
  searchText: undefined,
  setSearchText: undefined,
  selectedSchoolIds: undefined,
  setSelectedSchoolIds: undefined,
};

export default CoursesProvider;
